import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ClientLayout from "../components/ClientLayout.jsx";
import PlanCard from "../components/billing/PlanCard.jsx";
import PriceTag from "../components/billing/PriceTag.jsx";
import StatusPill from "../components/billing/StatusPill.jsx";
import GradientSummaryCard from "../components/billing/GradientSummaryCard.jsx";
import CheckoutRecoveryBanner from "../components/billing/CheckoutRecoveryBanner.jsx";
import InvoiceHistoryList from "../components/billing/InvoiceHistoryList.jsx";
import PaymentMethodCard from "../components/billing/PaymentMethodCard.jsx";
import { useCoachClientPlans } from "../hooks/useCoachClientPlans.js";
import { useClientCoachAccess } from "../hooks/useClientCoachAccess.js";
import { useCheckoutRecovery } from "../hooks/useCheckoutRecovery.js";
import {
  checkoutCoachClientPlan,
  createClientPortalSession,
  fetchClientInvoicesViaGateway,
  syncCoachCheckoutSessionFromStripeWithRetry,
} from "../lib/stripe.js";
import {
  consumePendingPortalReturn,
  stashPendingPortalReturn,
} from "../lib/portalReturn.js";
import {
  stashStripeCheckoutSessionId,
} from "../lib/checkoutSessionRecovery.js";
import {
  ClientBillingLoadingSkeleton,
  PlanCardSkeletonGrid,
} from "../components/loading/SkeletonBlocks.jsx";
import { formatBillingInstant } from "../lib/stripeInstant.js";
import { formatMoneyFromMinorUnits } from "../lib/planCatalog.js";

function planTypeLabel(plan) {
  if (plan.billingType === "one_time") {
    return `${plan.termWeeks}-week package`;
  }
  return plan.interval === "year" ? "Yearly plan" : "Monthly plan";
}

function purchaseLabel(purchase) {
  if (purchase.billingType === "one_time") {
    return purchase.termEnd ? `Access until ${formatBillingInstant(purchase.termEnd)}` : "One-time package";
  }
  if (purchase.currentPeriodEnd) {
    return `Renews ${formatBillingInstant(purchase.currentPeriodEnd)}`;
  }
  return "Active plan";
}

export default function ClientBillingPage() {
  const { plans, isLoading: plansLoading, error: plansError } = useCoachClientPlans();
  const { access, accessGranted, hasCoach, isLoading: accessLoading, error: accessError, refetch } =
    useClientCoachAccess();

  const [checkoutBusy, setCheckoutBusy] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (searchParams.get("coach_checkout") === "1" && sessionId) {
      stashStripeCheckoutSessionId(sessionId);
    }
  }, [searchParams]);

  const {
    pendingSessionId: pendingCoachCheckoutSessionId,
    recoverBusy,
    recoverErr,
    retry: retryCoachCheckoutRecovery,
  } = useCheckoutRecovery({
    subLoading: accessLoading,
    alreadyResolved: accessGranted,
    onRecovered: refetch,
    syncFn: syncCoachCheckoutSessionFromStripeWithRetry,
  });

  useEffect(() => {
    if (searchParams.get("coach_checkout") === "1") {
      searchParams.delete("coach_checkout");
      searchParams.delete("session_id");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (consumePendingPortalReturn() !== "/billing-settings") return;
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (!accessGranted) {
      setInvoices([]);
      setInvLoading(false);
      return;
    }
    let cancelled = false;
    setInvLoading(true);
    fetchClientInvoicesViaGateway(50)
      .then((data) => {
        if (!cancelled) setInvoices(data.invoices ?? []);
      })
      .catch(() => {
        if (!cancelled) setInvoices([]);
      })
      .finally(() => {
        if (!cancelled) setInvLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accessGranted]);

  const recurringPlans = plans.filter((p) => p.billingType === "recurring");
  const oneTimePlans = plans.filter((p) => p.billingType === "one_time");
  const activePurchase = (access?.purchases ?? []).find((p) => p.accessGranted) ?? null;

  async function handleCheckout(planId) {
    if (checkoutBusy) return;
    setCheckoutBusy(planId);
    setActionError(null);
    try {
      const { url } = await checkoutCoachClientPlan(planId);
      window.location.href = url;
    } catch (err) {
      setActionError(err?.message ?? String(err));
      setCheckoutBusy(null);
    }
  }

  async function openClientPortal() {
    setPortalBusy(true);
    setActionError(null);
    try {
      const { url } = await createClientPortalSession();
      if (url) {
        stashPendingPortalReturn("/billing-settings");
        window.location.assign(url);
      }
    } catch (err) {
      setActionError(err?.message ?? String(err));
    } finally {
      setPortalBusy(false);
    }
  }

  const hasRecurring = access?.hasRecurring;
  const hasOneTime = access?.hasOneTime;

  return (
    <ClientLayout>
      {pendingCoachCheckoutSessionId && (
        <CheckoutRecoveryBanner
          title="Finish activating your plan"
          description="Payment succeeded — we're saving your access now."
          busy={recoverBusy}
          error={recoverErr}
          onRetry={retryCoachCheckoutRecovery}
          retryLabel="Try again"
        />
      )}

      {actionError && (
        <p className="rounded-[10px] border border-hc-error/30 bg-hc-error/10 px-3 py-2 text-xs text-hc-error break-words">
          {actionError}
        </p>
      )}

      {accessLoading && <ClientBillingLoadingSkeleton />}

      {!accessLoading && accessGranted && activePurchase && (
        <GradientSummaryCard>
          <p className="text-xs text-hc-text-dim">Your coach plan</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-lg font-bold text-hc-text">
              {plans.find((p) => p.id === activePurchase.planId)?.name ?? "Active plan"}
            </span>
            <StatusPill variant="success">Active</StatusPill>
            <span className="text-xs text-hc-text-dim">{purchaseLabel(activePurchase)}</span>
          </div>
          <p className="mt-3 text-xs text-hc-text-dim">
            To cancel, contact your coach directly.
          </p>
        </GradientSummaryCard>
      )}

      {!accessLoading && !hasCoach && (
        <section className="rounded-2xl border border-dashed border-hc-surface-border bg-hc-surface/60 px-6 py-12 text-center">
          <h2 className="text-sm font-semibold tracking-[-0.15px] text-hc-text">
            You haven&apos;t connected with a coach yet
          </h2>
          <p className="mx-auto mt-2 max-w-md text-xs text-hc-text-dim">
            Ask your coach to add you with this email address. Once they do, their plans will appear here so you can subscribe.
          </p>
        </section>
      )}

      {!accessLoading && hasCoach && !accessGranted && (
        <section className="rounded-2xl border border-hc-surface-border bg-hc-surface p-6">
          <h2 className="mb-1 text-sm font-semibold tracking-[-0.15px] text-hc-text">
            Choose a plan from your coach
          </h2>
          <p className="mb-5 text-xs text-hc-text-dim">
            Choose a plan to unlock programs and coaching content. Prices are before tax; tax is added at checkout (CAD).
          </p>

          {(plansError || accessError) && (
            <p className="mb-4 text-xs text-hc-error break-words">
              {plansError ?? accessError}
            </p>
          )}

          {plansLoading ? (
            <PlanCardSkeletonGrid count={1} />
          ) : (
            <>
              {plans.length === 0 && (
                <p className="text-xs text-hc-text-dim">
                  Your coach has not published any plans yet. Check back soon.
                </p>
              )}

              {recurringPlans.length > 0 && (
                <div className="mb-6">
                  <p className="mb-3 text-xs font-medium text-hc-text-muted">Ongoing plans</p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {recurringPlans.map((plan) => (
                      <PlanCard
                        key={plan.id}
                        tone="shell"
                        title={plan.name}
                        badge={<StatusPill variant="muted" size="sm">{planTypeLabel(plan)}</StatusPill>}
                        footer={
                          <button
                            type="button"
                            disabled={!!checkoutBusy || hasRecurring}
                            onClick={() => handleCheckout(plan.id)}
                            className="mt-5 w-full rounded-[10px] bg-hc-primary px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
                          >
                            {checkoutBusy === plan.id
                              ? "Redirecting…"
                              : hasRecurring
                                ? "Plan active"
                                : "Get started"}
                          </button>
                        }
                      >
                        <PriceTag
                          amount={formatMoneyFromMinorUnits(plan.amountCents, plan.currency)}
                          suffix={plan.interval === "year" ? "/yr" : "/mo"}
                        />
                        {plan.description && (
                          <p className="mt-2 text-xs text-hc-text-dim">{plan.description}</p>
                        )}
                      </PlanCard>
                    ))}
                  </div>
                </div>
              )}

              {oneTimePlans.length > 0 && (
                <div>
                  <p className="mb-3 text-xs font-medium text-hc-text-muted">Packages</p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {oneTimePlans.map((plan) => (
                      <PlanCard
                        key={plan.id}
                        tone="shell"
                        title={plan.name}
                        badge={<StatusPill variant="muted" size="sm">{planTypeLabel(plan)}</StatusPill>}
                        footer={
                          <button
                            type="button"
                            disabled={!!checkoutBusy || hasOneTime}
                            onClick={() => handleCheckout(plan.id)}
                            className="mt-5 w-full rounded-[10px] bg-hc-primary px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
                          >
                            {checkoutBusy === plan.id
                              ? "Redirecting…"
                              : hasOneTime
                                ? "Package active"
                                : "Purchase"}
                          </button>
                        }
                      >
                        <PriceTag
                          amount={formatMoneyFromMinorUnits(plan.amountCents, plan.currency)}
                          suffix=" one-time"
                        />
                        {plan.description && (
                          <p className="mt-2 text-xs text-hc-text-dim">{plan.description}</p>
                        )}
                      </PlanCard>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {!accessLoading && accessGranted && (
        <>
          <section className="rounded-2xl border border-hc-surface-border bg-hc-surface p-6">
            <h2 className="mb-4 text-sm font-semibold tracking-[-0.15px] text-hc-text">
              Payment method
            </h2>
            <PaymentMethodCard
              cardBrand={access?.paymentMethod?.cardBrand}
              cardLast4={access?.paymentMethod?.cardLast4}
              cardExpMonth={access?.paymentMethod?.cardExpMonth}
              cardExpYear={access?.paymentMethod?.cardExpYear}
              portalBusy={portalBusy}
              canManage={hasRecurring}
              onUpdatePaymentMethod={openClientPortal}
              emptyMessage={
                hasRecurring
                  ? "No payment method on file."
                  : "Payment method management is available for ongoing plans."
              }
            />
          </section>
          <InvoiceHistoryList
            invoices={invoices}
            isLoading={invLoading}
            className="mt-4"
          />
          <section className="mt-4 rounded-2xl border border-hc-surface-border bg-hc-surface p-6">
            <h2 className="text-sm font-semibold text-hc-text">Need to make a change?</h2>
            <p className="mt-2 text-xs text-hc-text-dim">
              Contact your coach to change or cancel your plan.
            </p>
          </section>
        </>
      )}
    </ClientLayout>
  );
}
